import type { PollData } from './model.ts';

export interface PollOptionResult {
  id: string;
  label: string;
  /** People who picked (or scored) this option. */
  votes: number;
  /** votes / voters (0..1) for choice polls. */
  share: number;
  /** Average 1..5 score in score polls. */
  score: number | null;
  voterIds: string[];
}

export interface PollResults {
  voters: number;
  /** Sorted best first. */
  options: PollOptionResult[];
  /** Highest option (ties → first in original order); null without votes. */
  leader: string | null;
  tie: boolean;
}

/** Tally a poll. Works for single, multiple and score polls. */
export function pollResults(poll: PollData): PollResults {
  const entries = Object.entries(poll.votes);
  const voters = entries.length;
  const results: PollOptionResult[] = poll.options.map((o) => {
    const voterIds: string[] = [];
    let sum = 0;
    for (const [uid, v] of entries) {
      if (poll.mode === 'score') {
        const s = v.scores?.[o.id];
        if (s) {
          voterIds.push(uid);
          sum += s;
        }
      } else if (v.choices?.includes(o.id)) voterIds.push(uid);
    }
    return {
      id: o.id,
      label: o.label,
      votes: voterIds.length,
      share: voters ? voterIds.length / voters : 0,
      score: poll.mode === 'score' ? (voterIds.length ? sum / voterIds.length : null) : null,
      voterIds,
    };
  });
  const rank = (r: PollOptionResult) => (poll.mode === 'score' ? (r.score ?? 0) : r.votes);
  const order = new Map(poll.options.map((o, i) => [o.id, i]));
  const sorted = [...results].sort((a, b) => rank(b) - rank(a) || order.get(a.id)! - order.get(b.id)!);
  const top = sorted[0];
  const leader = top && rank(top) > 0 ? top.id : null;
  const tie = !!top && sorted.length > 1 && rank(sorted[1]!) === rank(top) && rank(top) > 0;
  return { voters, options: sorted, leader, tie };
}
